// Series picker grid for Formula Calendar
// Shows 5 motorsport series as selectable cards (no groups, no flags)
import { useTranslation } from "react-i18next";

type Team = {
  id: number;
  name: string;
  shortName: string;
  crestUrl: string;
};

type Props = {
  teams: Team[];
  selectedTeams: Team[];
  onToggle: (team: Team) => void;
  searchQuery: string;
};

export default function TeamGrid({ teams, selectedTeams, onToggle, searchQuery }: Props) {
  const { t } = useTranslation();
  const selectedIds = new Set(selectedTeams.map((tm) => tm.id));
  const isFiltered = searchQuery.trim().length > 0;

  const displayed = isFiltered
    ? teams.filter((s) => {
        const q = searchQuery.toLowerCase();
        return s.name.toLowerCase().includes(q) || s.shortName.toLowerCase().includes(q);
      })
    : teams;

  if (isFiltered && displayed.length === 0) {
    return (
      <div className="flex flex-col items-center py-16 text-muted-foreground" data-testid="no-results">
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="mb-3 opacity-40">
          <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
        </svg>
        <p className="text-sm">{t("teams.no_results", { query: searchQuery })}</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 stagger-children" data-testid="team-grid-filtered">
      {displayed.map((series) => (
        <button
          key={series.id}
          onClick={() => onToggle(series)}
          className={`
            relative flex flex-col items-center gap-3 px-4 py-5 rounded-xl border text-center
            transition-all duration-150 cursor-pointer group w-full
            hover-elevate
            ${selectedIds.has(series.id)
              ? "team-chip-selected border-primary/70 bg-primary/10"
              : "border-border/50 bg-card/60 hover:border-border text-foreground"
            }
          `}
          aria-pressed={selectedIds.has(series.id)}
          data-testid={`team-chip-${series.id}`}
        >
          {/* Series badge */}
          {series.crestUrl ? (
            <img
              src={series.crestUrl}
              alt={series.name}
              className="w-14 h-14 object-contain"
              loading="lazy"
            />
          ) : (
            <div className="w-14 h-14 rounded-full bg-muted/60 flex items-center justify-center text-lg font-bold text-muted-foreground">
              {series.shortName.slice(0, 2)}
            </div>
          )}

          {/* Name + code */}
          <div className="min-w-0">
            <div className="text-sm font-semibold leading-tight">{series.name}</div>
            <div className="text-xs text-muted-foreground leading-none mt-1">{series.shortName}</div>
          </div>

          {/* Selected checkmark */}
          {selectedIds.has(series.id) && (
            <div className="absolute top-2 end-2 w-5 h-5 rounded-full bg-primary/20 flex items-center justify-center">
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="hsl(3 85% 55%)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 6L9 17l-5-5"/>
              </svg>
            </div>
          )}
        </button>
      ))}
    </div>
  );
}
